import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import treeService from '../services/treeService';
import analysisService from '../services/analysisService';
import { motion } from 'framer-motion';

const TreeDetailsPage = () => {
  const { id: treeId } = useParams();

  // State
  const [tree, setTree] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadMessage, setUploadMessage] = useState('');
  
  // Derived Stats State
  const [stats, setStats] = useState({ age: '', carbon: 0, daysToNextUpdate: 0, progress: 0 });

  useEffect(() => {
    const fetchTree = async () => {
      try {
        const data = await treeService.getTreeById(treeId);
        setTree(data);
        calculateStats(data.plantingDate);
        setIsLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to fetch tree data');
        setIsLoading(false);
      }
    };
    fetchTree();
  }, [treeId]);

  // --- Statistical Logic ---
  const calculateStats = (plantingDateStr) => {
    const start = new Date(plantingDateStr);
    const now = new Date();
    const diffTime = Math.abs(now - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // 1. Calculate Age String
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    const days = (diffDays % 365) % 30;
    const ageString = `${years > 0 ? years + 'y ' : ''}${months}m ${days}d`;

    // 2. Calculate Carbon (Estimate: 22kg/year for average tropical tree)
    // Formula: (Days / 365) * 22kg
    const carbon = ((diffDays / 365) * 22).toFixed(2);

    // 3. Calculate Countdown to Next Monthly Update
    // Find the "day of month" it was planted (e.g., the 15th)
    const plantedDay = start.getDate();
    const currentDay = now.getDate();
    
    let daysLeft;
    if (currentDay < plantedDay) {
      daysLeft = plantedDay - currentDay;
    } else {
      // If we passed the date this month, distance is to next month
      const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      daysLeft = (daysInMonth - currentDay) + plantedDay;
    }

    // Progress bar percentage (assuming 30 day cycle)
    const progress = Math.min(100, Math.max(0, ((30 - daysLeft) / 30) * 100));

    setStats({
      age: ageString,
      carbon,
      daysToNextUpdate: daysLeft,
      progress
    });
  };

  const fileSelectHandler = (e) => {
    setSelectedFile(e.target.files[0]);
    setUploadMessage('');
  };

  const fileUploadHandler = async () => {
    if (!selectedFile) {
      alert('Please select a file first!');
      return;
    }
    setUploadMessage('Uploading...');
    try {
      const data = await analysisService.uploadTreeMedia(treeId, selectedFile);
      setUploadMessage(data.message);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Upload failed';
      setUploadMessage(`Error: ${msg}`);
    }
  };

  // --- Rendering ---
  if (isLoading) return <div className="text-center mt-20 text-brand-green font-bold text-xl">Loading Tree DNA...</div>;
  if (error) return <div className="text-center mt-20 text-red-500 font-bold">Error: {error}</div>;
  if (!tree) return <div className="text-center mt-20">Tree not found.</div>;

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* Header Card */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg border-t-8 border-brand-green overflow-hidden"
      >
        <div className="p-8 flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <span className="bg-brand-green text-white px-3 py-1 rounded-full text-xs font-mono font-bold tracking-wide">
                {tree.friendlyId || 'ID-PENDING'}
              </span>
              <span className="text-gray-500 text-sm">Planted: {new Date(tree.plantingDate).toLocaleDateString()}</span>
            </div>
            <h1 className="text-4xl font-bold text-brand-gray mb-1">{tree.species}</h1>
            <p className="text-brand-green-dark font-medium">📍 [{tree.location.coordinates[1]}, {tree.location.coordinates[0]}]</p>
          </div>
          <Link to="/" className="mt-4 md:mt-0 text-brand-gold hover:text-brand-green font-bold transition-colors">
            &larr; Back to Portfolio
          </Link>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        
        {/* Left Column: Statistics */}
        <motion.div 
           initial={{ opacity: 0, x: -20 }} 
           animate={{ opacity: 1, x: 0 }}
           transition={{ delay: 0.1 }}
           className="md:col-span-2 space-y-6"
        >
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-gray-500 text-sm uppercase tracking-wider font-bold">Tree Age</h3>
              <p className="text-3xl font-bold text-brand-green-dark mt-2">{stats.age}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-gray-500 text-sm uppercase tracking-wider font-bold">CO₂ Sequestered</h3>
              <p className="text-3xl font-bold text-brand-green-dark mt-2">{stats.carbon} <span className="text-lg text-gray-400">kg</span></p>
            </div>
          </div>

          {/* Countdown Card */}
          <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-brand-gold">
            <div className="flex justify-between items-end mb-2">
              <div>
                <h3 className="text-lg font-bold text-brand-gray">Next Photo Update</h3>
                <p className="text-sm text-gray-500">Keep your monitoring streak alive!</p>
              </div>
              <div className="text-right">
                <span className="text-4xl font-bold text-brand-gold">{stats.daysToNextUpdate}</span>
                <span className="text-gray-400 text-sm ml-1">days left</span>
              </div>
            </div>
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div 
                className="bg-brand-gold h-4 rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${stats.progress}%` }}
              ></div>
            </div>
          </div>
        </motion.div>

        {/* Right Column: Actions / Upload */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }} 
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 h-fit"
        >
          <h3 className="text-xl font-bold text-brand-gray mb-4 border-b pb-2">Update Tree Status</h3>
          
          <div className="space-y-4">
            <div className="bg-brand-white p-4 rounded border border-dashed border-brand-green text-center">
              <p className="text-sm text-gray-600 mb-3">Upload a clear photo of the tree for AI Analysis.</p>
              <input 
                type="file" 
                onChange={fileSelectHandler} 
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-brand-green-light file:text-brand-green-dark hover:file:bg-brand-green-light/80 cursor-pointer"
              />
            </div>

            <button 
              onClick={fileUploadHandler}
              className="w-full bg-brand-green hover:bg-brand-green-dark text-white font-bold py-3 rounded shadow-md transition-colors duration-200 flex justify-center items-center"
            >
              <span>Upload Update</span>
            </button>

            {uploadMessage && (
              <div className={`p-3 rounded text-sm text-center ${uploadMessage.includes('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-800'}`}>
                {uploadMessage}
              </div>
            )}
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default TreeDetailsPage;